import * as Y from 'yjs';
import { useReactFlow } from '@xyflow/react';
import type { CanvasUndoApi } from '@/features/canvas/engine/hooks/use-canvas-undo-manager';
import { useNodeActions } from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import { useCanvasDoc } from '@/features/canvas/engine/wiring/contexts/canvas-doc-context';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { serializeForClipboard, writeCanvasToClipboard } from '@/features/canvas/engine/primitives/clipboard';
import { useUndoRedoShortcut } from './use-undo-redo-shortcut';
import { useSelectAllShortcut } from './use-select-all-shortcut';
import { useDeleteShortcut } from './use-delete-shortcut';
import { useDuplicateShortcut } from './use-duplicate-shortcut';
import { useZOrderShortcuts } from './use-z-order-shortcuts';
import { useLockShortcut } from './use-lock-shortcut';
import { useNudgeShortcut } from './use-nudge-shortcut';
import { useCopyShortcut } from './use-copy-shortcut';
import { useCutShortcut } from './use-cut-shortcut';
import { useZoomShortcut } from './use-zoom-shortcut';
import { useGroupShortcut } from './use-group-shortcut';
import { useDeselectShortcut } from './use-deselect-shortcut';
import { createNode } from '@/features/canvas/engine/primitives/persistence/create-node';
import { absoluteToFrameLocal, frameLocalToAbsolute } from '@/features/canvas/engine/primitives/geometry/geometry';
import { readPoint, readSize, readChildOrder } from '@/features/canvas/engine/primitives/persistence/yjs-readers';

interface CanvasKeyboardShortcutsProps {
  undoApi: CanvasUndoApi;
  onNodesDelete: (ids: string[]) => void;
  onEdgesDelete?: (ids: string[]) => void;
}

export function CanvasKeyboardShortcuts({ undoApi, onNodesDelete, onEdgesDelete }: CanvasKeyboardShortcutsProps) {
  const actions = useNodeActions();
  const doc = useCanvasDoc();
  const binding = useCanvasBinding();
  const { setNodes } = useReactFlow();

  useUndoRedoShortcut(undoApi);
  useDeselectShortcut();
  useSelectAllShortcut(() => actions?.onSelectAll());
  useDeleteShortcut(onNodesDelete, onEdgesDelete);
  useDuplicateShortcut((ids) => {
    if (ids.length === 1) actions?.onDuplicate(ids[0]);
    else actions?.onDuplicateMany(ids);
  });
  useZOrderShortcuts({
    onBringToFront: (id) => actions?.onBringToFront(id),
    onSendToBack: (id) => actions?.onSendToBack(id),
    onBringForward: (id) => actions?.onBringForward(id),
    onSendBackward: (id) => actions?.onSendBackward(id),
  });
  useLockShortcut((id) => actions?.onToggleLock(id));
  useZoomShortcut();
  useCopyShortcut((ids) => {
    if (!doc) return;
    const nodes = (canvasRoots(doc, binding).nodes);
    const yNodes = ids.map((id) => nodes.get(id)).filter((n): n is Y.Map<unknown> => n != null);
    const json = serializeForClipboard(yNodes, []);
    writeCanvasToClipboard(json);
  });
  useCutShortcut((ids) => {
    if (!doc) return;
    const nodes = (canvasRoots(doc, binding).nodes);
    const yNodes = ids.map((id) => nodes.get(id)).filter((n): n is Y.Map<unknown> => n != null);
    const json = serializeForClipboard(yNodes, []);
    writeCanvasToClipboard(json);
    onNodesDelete(ids);
  });
  useGroupShortcut({
    onGroup: (ids) => {
      if (!doc) return;
      const nodes = (canvasRoots(doc, binding).nodes);
      // Compute bounding box of all nodes being grouped
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const id of ids) {
        const n = nodes.get(id);
        if (!n) continue;
        const pos = readPoint(n, 'position');
        if (!pos) continue;
        const size = readSize(n);
        const w = size?.width ?? 160;
        const h = size?.height ?? 120;
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + w);
        maxY = Math.max(maxY, pos.y + h);
      }
      const PADDING = 16;
      const framePos = { x: minX - PADDING, y: minY - PADDING };
      const frameSize = { width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 };
      // createNode adds the frame to the doc in a transact; then we add children
      const frame = createNode(doc, 'frame', framePos, undefined, doc.clientID, frameSize, binding);
      const frameId = frame.get('id') as string;
      doc.transact(() => {
        const childOrder = readChildOrder(frame);
        if (!childOrder) return;
        for (const id of ids) {
          const n = nodes.get(id);
          if (!n) continue;
          const absPos = readPoint(n, 'position');
          if (!absPos) continue;
          n.set('parentId', frameId);
          n.set('position', absoluteToFrameLocal(absPos, framePos));
          if (!childOrder.toArray().includes(id)) childOrder.push([id]);
        }
      }, doc.clientID);
    },
    onUngroup: (frameIds) => {
      if (!doc) return;
      const nodes = (canvasRoots(doc, binding).nodes);
      doc.transact(() => {
        for (const frameId of frameIds) {
          const frame = nodes.get(frameId);
          if (!frame) continue;
          if (frame.get('locked') === true) continue;
          const framePos = readPoint(frame, 'position');
          if (!framePos) continue;
          const childOrder = readChildOrder(frame);
          if (!childOrder) continue;
          for (const childId of childOrder.toArray()) {
            const child = nodes.get(childId);
            if (!child) continue;
            const local = readPoint(child, 'position');
            if (!local) continue;
            child.set('parentId', null);
            child.set('position', frameLocalToAbsolute(framePos, local));
          }
          const zOrder = canvasRoots(doc, binding).zOrder;
          const arr = zOrder.toArray();
          const idx = arr.indexOf(frameId);
          if (idx !== -1) zOrder.delete(idx, 1);
          nodes.delete(frameId);
        }
      }, doc.clientID);
    },
  });
  useNudgeShortcut((ids, dx, dy) => {
    if (!doc) return;
    const yjsNodes = (canvasRoots(doc, binding).nodes);
    const idSet = new Set(ids);
    // Update RF local state immediately so there's no visual snap-back.
    // The Yjs write below will round-trip through useNodesFromYjs and
    // merge() — without this setNodes call, RF would render the old
    // position for one frame while Yjs processes the transact.
    setNodes((prev) =>
      prev.map((n) => {
        if (!idSet.has(n.id)) return n;
        const yNode = yjsNodes.get(n.id);
        if (yNode?.get('locked') === true) return n;
        return { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } };
      })
    );
    doc.transact(() => {
      for (const id of ids) {
        const node = yjsNodes.get(id);
        if (!node) continue;
        if (node.get('locked') === true) continue;
        const pos = readPoint(node, 'position');
        if (!pos) continue;
        node.set('position', { x: pos.x + dx, y: pos.y + dy });
      }
    }, doc.clientID);
  });

  return null;
}
